import React, { useMemo } from 'react';

import { computeText } from '@/locales/utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { Text } from '@/components/atoms/Text';
import { SvyButton } from '@/components/savory/SvyButton';
import { confirm } from '@/core/asyncActions/confirm';
import { useLinkProductsSkusToExternalIdsMappingsMutation } from '@/services/innovorder/adminTools/adminTools.endpoints';
import { Form } from '@/core/form/Form';
import { useGetBrandRestaurantsQuery } from '@/services/innovorder/brand/brand.endpoints';
import { useGetMenusQuery } from '@/services/innovorder/menu/menu.endpoints';
import { useParams } from 'react-router';
import { Select } from '@/components/form/Select';
import { FormControl, FormControls, SuccessMessage, ErrorMessage } from '../AdminTools.style';
import { safeMutate } from '@/utils/errors';
import { useFormContext } from 'react-hook-form';

interface ParamTypes {
    brandId: string;
}

export type LinkProductsSkusToExternalIdsMappingsInputs = {
    restaurantId: number;
    menuId?: number;
};

interface MenuSelectProps {
    restaurantId: number | undefined;
    brandId: number;
}

const MenuSelect: React.FunctionComponent<MenuSelectProps> = ({ restaurantId, brandId }) => {
    const intl = useIntl();
    const previousRestaurantIdRef = React.useRef<number | undefined>();
    const { resetField } = useFormContext<LinkProductsSkusToExternalIdsMappingsInputs>();

    const { data: menus, isLoading: isLoadingMenus } = useGetMenusQuery(
        {
            restaurantId: restaurantId!,
            brandId,
        },
        { skip: !restaurantId },
    );

    const menuOptions = useMemo(
        () =>
            (menus || []).map((menu) => ({
                label: menu.name,
                value: menu.menuId,
            })),
        [menus],
    );

    // Reset menu selection when restaurant changes
    React.useEffect(() => {
        if (
            restaurantId !== undefined &&
            previousRestaurantIdRef.current !== undefined &&
            previousRestaurantIdRef.current !== restaurantId
        ) {
            // Reset to empty value - react-hook-form will handle the validation
            resetField('menuId', { defaultValue: undefined });
        }
        previousRestaurantIdRef.current = restaurantId;
    }, [restaurantId, resetField]);

    return (
        <Select
            labelId="brand.adminTools.linkProductsSkusToExternalIdsMappings.form.menu"
            options={menuOptions}
            name="menuId"
            rules={{ required: computeText(intl, 'field.required') }}
            isLoading={isLoadingMenus}
            disabled={!restaurantId}
        />
    );
};

const LinkProductsSkusToExternalIdsMappings: React.FunctionComponent = () => {
    const intl = useIntl();
    const { brandId } = useParams<ParamTypes>();
    const brandIdNumber = parseInt(brandId, 10);

    const [linkProductsSkusToExternalIdsMappings, { isSuccess, data }] =
        useLinkProductsSkusToExternalIdsMappingsMutation();

    const { data: restaurants, isLoading: isLoadingRestaurants } = useGetBrandRestaurantsQuery({
        brandId: brandIdNumber,
    });

    const restaurantOptions = useMemo(
        () =>
            (restaurants || []).map((restaurant) => ({
                label: restaurant.name,
                value: restaurant.restaurantId,
            })),
        [restaurants],
    );

    const handleSubmitForm = async ({ restaurantId, menuId }: LinkProductsSkusToExternalIdsMappingsInputs) => {
        if (!menuId) {
            return;
        }
        const hasConfirmed = await confirm(computeText(intl, 'adminTools.confirmModal.description'));
        if (hasConfirmed) {
            await safeMutate(
                linkProductsSkusToExternalIdsMappings({
                    brandId: brandIdNumber,
                    restaurantId,
                    menuId,
                }),
            );
        }
    };

    return (
        <Form<LinkProductsSkusToExternalIdsMappingsInputs>>
            {({ handleSubmit, watch, setValue }) => {
                const watchedRestaurantId = watch('restaurantId') as number | undefined;

                return (
                    <>
                        <Text type="large" text="brand.adminTools.linkProductsSkusToExternalIdsMappings.title" />
                        <br />
                        <Text text="brand.adminTools.linkProductsSkusToExternalIdsMappings.description" />
                        <FormControls>
                            <FormControl>
                                <Select
                                    labelId="brand.adminTools.linkProductsSkusToExternalIdsMappings.form.restaurant"
                                    options={restaurantOptions}
                                    name="restaurantId"
                                    rules={{ required: computeText(intl, 'field.required') }}
                                    isLoading={isLoadingRestaurants}
                                />
                                <MenuSelect restaurantId={watchedRestaurantId} brandId={brandIdNumber} />
                            </FormControl>
                            <SvyButton onClick={handleSubmit(handleSubmitForm)}>
                                <FormattedMessage id="button.validate" />
                            </SvyButton>
                            {isSuccess && data?.success && (
                                <SuccessMessage text="brand.adminTools.linkProductsSkusToExternalIdsMappings.form.success" />
                            )}
                            {isSuccess && !data?.success && (
                                <ErrorMessage text="brand.adminTools.linkProductsSkusToExternalIdsMappings.form.error" />
                            )}
                            {isSuccess && data?.message && <Text text={data.message} type="small" />}
                        </FormControls>
                    </>
                );
            }}
        </Form>
    );
};

export default LinkProductsSkusToExternalIdsMappings;
